import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from '../../utils/notify';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import { discoveryService } from '../../services/discoveryService';
import { settingsService } from '../../services/settingsService';
import { Modal, SearchUserCard } from '../common';
import { Button, SecondaryButton } from '../common/Button';
import VipIcon from '../Icons/VipIcons';

/*
 * UserOnboarding — full-screen onboarding modal that walks users through
 * the three onboarding steps inline. Uses the shared <Modal> primitive so
 * behavior (portal, overlay-click, escape, scroll lock) matches the rest
 * of the app.
 *
 * Replaces the old GlobalCreatorBanner + CreatorBanner pair.
 */

const DotsRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
`;

const Dot = styled.span`
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  transition: background 0.25s ease;
`;

const CreatorCarousel = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 4px 12px;
  margin: 0 -4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CarouselCard = styled.div`
  flex-shrink: 0;
  width: 200px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 16px;

  span {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 6px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.bg2};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.colors.primary};
  }
`;

const Hint = styled.small`
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const CreatorPitch = styled.div`
  background: ${(p) => p.theme.colors.bg2};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  padding: 24px;
  border-radius: 12px;
  text-align: center;
`;

const PitchIconWrap = styled.div`
  color: ${(p) => p.theme.colors.primary};
  margin-bottom: 4px;
`;

const PitchHeading = styled.h3`
  margin: 8px 0 8px;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
`;

const PitchBody = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.colors.textSecondary};
`;

/* ---- Step bodies ---- */

const FollowCreatorsStep = () => {
  const [creators, setCreators] = useState(null);

  useEffect(() => {
    let mounted = true;
    discoveryService
      .getSuggestedCreators(8)
      .then((data) => mounted && setCreators(data || []))
      .catch(() => mounted && setCreators([]));
    return () => {
      mounted = false;
    };
  }, []);

  if (creators === null) return <EmptyState>Loading suggestions…</EmptyState>;
  if (creators.length === 0) {
    return <EmptyState>No suggestions available yet. You can skip and explore later.</EmptyState>;
  }
  return (
    <CreatorCarousel>
      {creators.map((user) => (
        <CarouselCard key={user.id}>
          <SearchUserCard user={user} showStats={false} showActions avatarSize="44px" />
        </CarouselCard>
      ))}
    </CreatorCarousel>
  );
};

const ProfileStep = ({ formRef }) => {
  const { currentUser } = useAuth();
  const [displayName, setDisplayName] = useState(currentUser?.fullName || '');
  const [username, setUsername] = useState(currentUser?.username || '');

  useEffect(() => {
    formRef.current = { displayName, username };
  }, [displayName, username, formRef]);

  const usernameChanged = username.trim() !== (currentUser?.username || '').trim();

  return (
    <>
      <Field>
        <span>Display name</span>
        <Input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name"
          maxLength={60}
        />
      </Field>
      <Field>
        <span>Username</span>
        <Input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder="username"
          maxLength={30}
        />
        {usernameChanged && (
          <Hint>Changing your username will log you out — you'll need to sign in again with the new one.</Hint>
        )}
      </Field>
    </>
  );
};

const BecomeCreatorStep = () => (
  <CreatorPitch>
    <PitchIconWrap>
      <VipIcon name="StarIcon" size={32} />
    </PitchIconWrap>
    <PitchHeading>Turn your content into income</PitchHeading>
    <PitchBody>
      Set up subscription tiers, share exclusive posts, and connect with your fans. We'll walk you through
      verification and payouts in Settings.
    </PitchBody>
  </CreatorPitch>
);

/* ---- Orchestrator ---- */

const UserOnboarding = () => {
  const navigate = useNavigate();
  const { currentUser, updateUser } = useAuth();
  const { activeStep, completeStep, skipStep } = useOnboarding();
  const [saving, setSaving] = useState(false);
  const profileFormRef = React.useRef({ displayName: '', username: '' });

  const handleClose = useCallback(() => {
    if (activeStep) skipStep(activeStep.id);
  }, [activeStep, skipStep]);

  const handleContinue = useCallback(async () => {
    if (!activeStep) return;

    if (activeStep.id === 'set-profile') {
      const { displayName, username } = profileFormRef.current;
      const trimmedName = (displayName || '').trim();
      const trimmedUser = (username || '').trim();
      if (!trimmedName || !trimmedUser) {
        toast.error('Please fill in both fields');
        return;
      }
      const displayChanged = trimmedName !== (currentUser?.fullName || '').trim();
      const usernameChanged = trimmedUser !== (currentUser?.username || '').trim();
      setSaving(true);
      try {
        if (displayChanged) {
          await settingsService.updateUserSettings({ profile: { displayName: trimmedName } });
          if (typeof updateUser === 'function') updateUser({ fullName: trimmedName });
        }
        if (usernameChanged) {
          await settingsService.updateUserSettings({ profile: { username: trimmedUser } });
          completeStep(activeStep.id);
          toast.success('Username changed! Please log in again.');
          setTimeout(() => {
            document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            window.location.href = '/login';
          }, 1200);
          return;
        }
        completeStep(activeStep.id);
        toast.success('Profile updated');
      } catch (err) {
        toast.error(err?.message || 'Failed to update profile');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (activeStep.id === 'become-creator') {
      completeStep(activeStep.id);
      navigate('/settings', { state: { activeTab: 'creator' } });
      return;
    }

    completeStep(activeStep.id);
  }, [activeStep, completeStep, currentUser, navigate, updateUser]);

  if (!activeStep) return null;

  const primaryLabel = (() => {
    if (activeStep.id === 'set-profile') return saving ? 'Saving…' : 'Save & continue';
    if (activeStep.id === 'become-creator') return 'Get started';
    if (activeStep.index === activeStep.total - 1) return 'Done';
    return 'Continue';
  })();

  const footer = (
    <>
      <SecondaryButton onClick={handleClose} disabled={saving}>
        Skip for now
      </SecondaryButton>
      <Button onClick={handleContinue} disabled={saving}>
        {primaryLabel}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={activeStep.title}
      subtitle={activeStep.subtitle}
      footer={footer}
      maxWidth="560px"
      closeOnOverlayClick={false}
    >
      <DotsRow>
        {Array.from({ length: activeStep.total }).map((_, i) => (
          <Dot key={i} $active={i <= activeStep.index} />
        ))}
      </DotsRow>
      {activeStep.id === 'follow-creators' && <FollowCreatorsStep />}
      {activeStep.id === 'set-profile' && <ProfileStep formRef={profileFormRef} />}
      {activeStep.id === 'become-creator' && <BecomeCreatorStep />}
    </Modal>
  );
};

export default UserOnboarding;
